/**
 * primitives.ts — Primitivos visuais e seção com camadas do Studio
 *
 * Estes blocos são a base do novo motor de modelos: em vez de "capas fechadas"
 * com poucas opções, uma capa passa a ser uma SEÇÃO COM CAMADAS contendo
 * primitivos (selo, título, texto, botões, imagem, cartão, avatares, número,
 * logos, moldura de dispositivo). Assim é possível empilhar fundo + camada
 * escura + brilho + cartão flutuante e ainda manter tudo editável pelo painel
 * de propriedades, que continua gerado automaticamente pelo catálogo.
 *
 * Cada bloco registrado aqui TEM de ter componente web
 * (`src/lib/studio/render/web/primitiveBlocks.tsx`) e compilador HTML
 * (`supabase/functions/_shared/studio/htmlRenderer.ts`).
 *
 * RELEASE: v1.4.0 - 2026-09-11 - Recorte, traçado próprio, inclinação e bloco
 *            "Forma decorativa" ligados ao registro visual (Fase 6)
 * RELEASE: v1.3.0 - 2026-09-11 - Movimento ligado ao registro versionado, com
 *            disparo, atraso e duração (Fase 5)
 * RELEASE: v1.2.0 - 2026-09-11 - Propriedade "Efeito visual" ligada ao registro
 *            versionado de efeitos (Fase 4)
 * RELEASE: v1.1.0 - 2026-09-11 - Camadas e posicionamento governado: grupo
 *            "Camadas" nos primitivos e chave "Camadas livres" na seção (Fase 3)
 * RELEASE: v1.0.0 - 2026-09-10 - Versão inicial (Studio, motor de modelos — Fase 1)
 */

import { getBlock, listBlocks, registerBlock, registerBlocks } from '../blockRegistry';
import {
  STUDIO_ANCHOR_OPTIONS,
  STUDIO_DEPTH_OPTIONS,
  STUDIO_POSITION_OPTIONS,
} from '../layout/layers';
import { STUDIO_EFFECT_OPTIONS } from '../effects';
import {
  STUDIO_MASK_OPTIONS,
  STUDIO_SHAPE_OPTIONS,
  STUDIO_SHAPE_SIZE_OPTIONS,
  STUDIO_TILT_OPTIONS,
} from '../visual';
import {
  STUDIO_MOTION_DELAY_OPTIONS,
  STUDIO_MOTION_DURATION_OPTIONS,
  STUDIO_MOTION_OPTIONS,
  STUDIO_MOTION_TRIGGER_OPTIONS,
} from '../motion';
import { STUDIO_ICON_OPTIONS } from '../icons';
import type { StudioBlockProp, StudioOutput } from '../types';


/** Primitivos que sabem se desenhar em qualquer forma de entrega. */
const TODAS: StudioOutput[] = ['web', 'pdf', 'email'];
/** Recursos que só fazem sentido no navegador. */
const SO_WEB: StudioOutput[] = ['web'];

/** Alinhamento horizontal reaproveitado pelos primitivos. */
const ALINHAMENTO = [
  { value: 'left', label: 'Esquerda' },
  { value: 'center', label: 'Centro' },
  { value: 'right', label: 'Direita' },
];

/**
 * Movimento de entrada — camada opcional e declarativa.
 * O desenho respeita "reduzir movimento" do sistema operacional.
 */
export const MOVIMENTO: StudioBlockProp = {
  key: 'motion',
  label: 'Movimento',
  type: 'select',
  defaultValue: 'none',
  options: STUDIO_MOTION_OPTIONS,
  help: 'Animação leve, feita só com estilo. Desligada na impressão, no e-mail e para quem pediu menos movimento.',
};

/** Quando a animação acontece: ao abrir a página ou ao rolar até o bloco. */
export const MOVIMENTO_DISPARO: StudioBlockProp = {
  key: 'motionTrigger',
  label: 'Quando animar',
  type: 'select',
  defaultValue: 'load',
  advanced: true,
  options: STUDIO_MOTION_TRIGGER_OPTIONS,
  help: 'Rolagem só vale para animações que acontecem uma vez.',
};

/** Atraso, para escalonar a entrada de vários blocos. */
export const MOVIMENTO_ATRASO: StudioBlockProp = {
  key: 'motionDelay',
  label: 'Atraso',
  type: 'select',
  defaultValue: '0',
  advanced: true,
  options: STUDIO_MOTION_DELAY_OPTIONS,
};

/** Velocidade da animação. */
export const MOVIMENTO_DURACAO: StudioBlockProp = {
  key: 'motionDuration',
  label: 'Velocidade',
  type: 'select',
  defaultValue: 'normal',
  advanced: true,
  options: STUDIO_MOTION_DURATION_OPTIONS,
};

/** Arredondamento comum a imagens e cartões. */
const RAIO = {
  key: 'radius',
  label: 'Cantos',
  type: 'select' as const,
  defaultValue: 'lg',
  options: [
    { value: 'none', label: 'Reto' },
    { value: 'sm', label: 'Leve' },
    { value: 'lg', label: 'Arredondado' },
    { value: 'xl', label: 'Bem arredondado' },
    { value: 'full', label: 'Circular' },
  ],
};

/** Sombra comum a imagens e cartões. */
const SOMBRA = {
  key: 'shadow',
  label: 'Sombra',
  type: 'select' as const,
  defaultValue: 'md',
  options: [
    { value: 'none', label: 'Sem sombra' },
    { value: 'sm', label: 'Discreta' },
    { value: 'md', label: 'Média' },
    { value: 'lg', label: 'Forte' },
    { value: 'glow', label: 'Brilho' },
  ],
};

registerBlocks([
  // ==========================================================================
  // Seção com camadas — o "palco" das composições
  // ==========================================================================
  {
    key: 'section.layered',
    label: 'Seção com camadas',
    family: 'layout',
    icon: 'layers',
    outputs: TODAS,
    container: true,
    props: [
      { key: 'backgroundColor', label: 'Cor de fundo', type: 'color' },
      { key: 'backgroundImage', label: 'Imagem de fundo', type: 'image' },
      {
        key: 'backgroundGradient',
        label: 'Gradiente de fundo',
        type: 'select',
        defaultValue: 'none',
        options: [
          { value: 'none', label: 'Nenhum' },
          { value: 'mesh-dark', label: 'Malha escura' },
          { value: 'mesh-brand', label: 'Malha da marca' },
          { value: 'sunset', label: 'Entardecer' },
          { value: 'aurora', label: 'Aurora' },
          { value: 'soft', label: 'Suave' },
        ],
      },
      {
        key: 'overlay',
        label: 'Camada sobre o fundo',
        type: 'select',
        defaultValue: 'none',
        options: [
          { value: 'none', label: 'Nenhuma' },
          { value: 'dark', label: 'Escurecer' },
          { value: 'darker', label: 'Escurecer muito' },
          { value: 'light', label: 'Clarear' },
          { value: 'bottom', label: 'Escurecer embaixo' },
          { value: 'left', label: 'Escurecer à esquerda' },
        ],
      },
      {
        key: 'pattern',
        label: 'Textura decorativa',
        type: 'select',
        defaultValue: 'none',
        options: [
          { value: 'none', label: 'Nenhuma' },
          { value: 'grid', label: 'Grade' },
          { value: 'dots', label: 'Pontos' },
          { value: 'rays', label: 'Raios de luz' },
        ],
      },
      {
        key: 'glow',
        label: 'Brilho decorativo',
        type: 'select',
        defaultValue: 'none',
        options: [
          { value: 'none', label: 'Nenhum' },
          { value: 'top-right', label: 'Canto superior direito' },
          { value: 'center', label: 'Centro' },
          { value: 'bottom-left', label: 'Canto inferior esquerdo' },
        ],
      },
      { key: 'textColor', label: 'Cor do texto', type: 'color' },
      {
        key: 'layout',
        label: 'Disposição do conteúdo',
        type: 'select',
        defaultValue: 'stack',
        options: [
          { value: 'stack', label: 'Empilhado' },
          { value: 'split', label: 'Duas colunas' },
          { value: 'split-reverse', label: 'Duas colunas (invertidas)' },
          { value: 'split-wide-left', label: 'Coluna maior à esquerda' },
          { value: 'bento', label: 'Mosaico (bento)' },
        ],
        help: 'No celular as colunas viram uma abaixo da outra.',
      },
      {
        key: 'minHeight',
        label: 'Altura',
        type: 'select',
        defaultValue: 'auto',
        options: [
          { value: 'auto', label: 'Conforme o conteúdo' },
          { value: 'md', label: 'Média' },
          { value: 'lg', label: 'Alta' },
          { value: 'screen', label: 'Tela inteira' },
        ],
      },
      {
        key: 'paddingY',
        label: 'Respiro em cima e embaixo',
        type: 'select',
        defaultValue: 'lg',
        options: [
          { value: 'sm', label: 'Pequeno' },
          { value: 'md', label: 'Médio' },
          { value: 'lg', label: 'Grande' },
          { value: 'xl', label: 'Muito grande' },
        ],
      },
      {
        key: 'bleed',
        label: 'Largura da faixa',
        type: 'select',
        defaultValue: 'full',
        options: [
          { value: 'full', label: 'Borda a borda' },
          { value: 'contained', label: 'Com margem lateral' },
        ],
        help: 'Borda a borda deixa a faixa encostar nas laterais da tela, como nas páginas profissionais. Use margem lateral em vídeo, formulário e contato.',
      },
      { key: 'maxWidth', label: 'Largura do miolo', type: 'text', defaultValue: '1120px' },
      { key: 'align', label: 'Alinhamento', type: 'select', defaultValue: 'left', options: ALINHAMENTO },
      { ...RAIO, defaultValue: 'none' },
      MOVIMENTO,
    ],
  },

  // ==========================================================================
  // Primitivos de conteúdo
  // ==========================================================================
  {
    key: 'ui.badge',
    label: 'Selo',
    family: 'content',
    icon: 'badge-check',
    outputs: TODAS,
    props: [
      { key: 'text', label: 'Texto', type: 'text', defaultValue: 'Novidade' },
      { key: 'prefix', label: 'Símbolo antes do texto', type: 'text', help: 'Ex.: ★, •, →' },
      {
        key: 'variant',
        label: 'Estilo',
        type: 'select',
        defaultValue: 'soft',
        options: [
          { value: 'soft', label: 'Suave' },
          { value: 'outline', label: 'Contornado' },
          { value: 'solid', label: 'Sólido' },
          { value: 'glass', label: 'Vidro' },
          { value: 'line', label: 'Editorial (traço + texto)' },

        ],
      },
      { key: 'accentColor', label: 'Cor de destaque', type: 'color' },
      { key: 'align', label: 'Alinhamento', type: 'select', defaultValue: 'left', options: ALINHAMENTO },
      MOVIMENTO,
    ],
  },

  {
    key: 'ui.title',
    label: 'Título',
    family: 'content',
    icon: 'heading-1',
    outputs: TODAS,
    props: [
      { key: 'text', label: 'Texto', type: 'text', defaultValue: 'Um título que convence' },
      { key: 'highlight', label: 'Trecho em destaque', type: 'text', help: 'Parte do título que recebe a cor de destaque.' },
      {
        key: 'size',
        label: 'Tamanho',
        type: 'select',
        defaultValue: 'xl',
        options: [
          { value: 'md', label: 'Médio' },
          { value: 'lg', label: 'Grande' },
          { value: 'xl', label: 'Muito grande' },
          { value: 'display', label: 'Gigante' },
          { value: 'hero', label: 'Capa (maior de todos)' },
        ],
      },
      {
        key: 'level',
        label: 'Nível do título',
        type: 'select',
        defaultValue: 'h1',
        options: [
          { value: 'h1', label: 'Principal (h1)' },
          { value: 'h2', label: 'Seção (h2)' },
          { value: 'h3', label: 'Subtítulo (h3)' },
        ],
      },
      { key: 'align', label: 'Alinhamento', type: 'select', defaultValue: 'left', options: ALINHAMENTO },
      { key: 'color', label: 'Cor', type: 'color' },
      { key: 'accentColor', label: 'Cor do destaque', type: 'color' },
      {
        key: 'transform',
        label: 'Caixa',
        type: 'select',
        defaultValue: 'inherit',
        advanced: true,
        options: [
          { value: 'inherit', label: 'Como no tema' },
          { value: 'none', label: 'Normal' },
          { value: 'uppercase', label: 'Tudo em maiúsculas' },
        ],
      },
      {
        key: 'weight',
        label: 'Peso',
        type: 'select',
        defaultValue: 'inherit',
        advanced: true,
        options: [
          { value: 'inherit', label: 'Como no tema' },
          { value: '400', label: 'Leve' },
          { value: '600', label: 'Médio' },
          { value: '700', label: 'Forte' },
          { value: '800', label: 'Muito forte' },
          { value: '900', label: 'Máximo' },
        ],
      },
      {
        key: 'leading',
        label: 'Entrelinha',
        type: 'select',
        defaultValue: 'normal',
        advanced: true,
        options: [
          { value: 'tight', label: 'Justa' },
          { value: 'normal', label: 'Normal' },
          { value: 'loose', label: 'Folgada' },
        ],
      },
      { key: 'maxWidth', label: 'Largura máxima', type: 'text', advanced: true, example: '14ch' },
      {
        key: 'preserveLineBreaks',
        label: 'Respeitar quebras de linha do texto',
        type: 'boolean',
        defaultValue: false,
        advanced: true,
        help: 'Quando ligado, cada quebra digitada no texto vira uma linha nova no título.',
      },
      MOVIMENTO,

    ],
  },

  {
    key: 'ui.paragraph',
    label: 'Parágrafo',
    family: 'content',
    icon: 'text',
    outputs: TODAS,
    props: [
      {
        key: 'text',
        label: 'Texto',
        type: 'richtext',
        defaultValue: 'Explique em poucas linhas o que a sua empresa entrega e por que vale a pena.',
      },
      {
        key: 'size',
        label: 'Tamanho',
        type: 'select',
        defaultValue: 'md',
        options: [
          { value: 'sm', label: 'Pequeno' },
          { value: 'md', label: 'Médio' },
          { value: 'lg', label: 'Grande' },
        ],
      },
      { key: 'align', label: 'Alinhamento', type: 'select', defaultValue: 'left', options: ALINHAMENTO },
      { key: 'maxWidth', label: 'Largura máxima', type: 'text', defaultValue: '560px' },
      { key: 'color', label: 'Cor', type: 'color' },
      MOVIMENTO,
    ],
  },

  {
    key: 'ui.buttons',
    label: 'Botões',
    family: 'action',
    icon: 'mouse-pointer-click',
    outputs: TODAS,
    props: [
      {
        key: 'items',
        label: 'Botões',
        type: 'richtext',
        defaultValue: 'Falar com um especialista|#formulario|solid\nVer como funciona|#recursos|ghost',
        help: 'Uma linha por botão: Texto | destino | estilo (solid, outline, ghost, glass).',
        syntax: 'Um botão por linha: texto|destino|estilo. Estilo aceita solid, outline, ghost ou glass.',
        example: 'Falar com um especialista|#formulario|solid\nVer como funciona|#recursos|ghost',
      },
      { key: 'align', label: 'Alinhamento', type: 'select', defaultValue: 'left', options: ALINHAMENTO },
      {
        key: 'size',
        label: 'Tamanho',
        type: 'select',
        defaultValue: 'md',
        options: [
          { value: 'sm', label: 'Pequeno' },
          { value: 'md', label: 'Médio' },
          { value: 'lg', label: 'Grande' },
        ],
      },
      { key: 'accentColor', label: 'Cor principal', type: 'color' },
      {
        key: 'shape',
        label: 'Cantos',
        type: 'select',
        defaultValue: 'rounded',
        options: [
          { value: 'rounded', label: 'Arredondados' },
          { value: 'square', label: 'Retos (editorial)' },
          { value: 'pill', label: 'Totalmente redondos' },
        ],
      },
      {
        key: 'uppercase',
        label: 'Texto em maiúsculas',
        type: 'boolean',
        defaultValue: false,
      },
      {
        key: 'arrow',
        label: 'Seta no fim do texto',
        type: 'boolean',
        defaultValue: false,
        advanced: true,
      },
      MOVIMENTO,

    ],
  },

  {
    key: 'ui.image',
    label: 'Imagem',
    family: 'content',
    icon: 'image',
    outputs: TODAS,
    props: [
      { key: 'src', label: 'Imagem', type: 'image' },
      { key: 'alt', label: 'Descrição da imagem', type: 'text' },
      {
        key: 'ratio',
        label: 'Proporção',
        type: 'select',
        defaultValue: 'auto',
        options: [
          { value: 'auto', label: 'Original' },
          { value: 'square', label: 'Quadrada' },
          { value: 'landscape', label: 'Paisagem (4:3)' },
          { value: 'wide', label: 'Panorâmica (16:9)' },
          { value: 'portrait', label: 'Retrato (3:4)' },
        ],
      },
      RAIO,
      SOMBRA,
      { key: 'width', label: 'Largura', type: 'text', defaultValue: '100%' },
      MOVIMENTO,
    ],
  },

  {
    key: 'ui.card',
    label: 'Cartão',
    family: 'content',
    icon: 'square',
    outputs: TODAS,
    props: [
      { key: 'icon', label: 'Símbolo', type: 'text', help: 'Ex.: ★, ✓, →' },
      {
        key: 'iconName',
        label: 'Ícone do sistema',
        type: 'select',
        defaultValue: '',
        options: STUDIO_ICON_OPTIONS,
        help: 'Desenho vetorial do próprio sistema. Tem preferência sobre o símbolo digitado.',
      },
      { key: 'eyebrow', label: 'Chapéu', type: 'text', help: 'Texto curto acima do título, em maiúsculas.' },
      { key: 'index', label: 'Número do cartão', type: 'text', help: 'Ex.: 01, 02, 03.' },
      { key: 'title', label: 'Título', type: 'text', defaultValue: 'Resultado em 30 dias' },
      { key: 'text', label: 'Texto', type: 'text', defaultValue: 'Acompanhamento próximo do início ao fim.' },
      {
        key: 'variant',
        label: 'Estilo',
        type: 'select',
        defaultValue: 'solid',
        options: [
          { value: 'solid', label: 'Sólido' },
          { value: 'outline', label: 'Contornado' },
          { value: 'glass', label: 'Vidro' },
          { value: 'editorial', label: 'Editorial (ícone em moldura)' },
        ],
      },
      {
        key: 'highlighted',
        label: 'Cartão em destaque',
        type: 'boolean',
        defaultValue: false,
        help: 'Aplica a cor de destaque na borda e um leve realce.',
      },
      {
        key: 'minHeight',
        label: 'Altura mínima',
        type: 'select',
        defaultValue: 'auto',
        advanced: true,
        options: [
          { value: 'auto', label: 'Conforme o conteúdo' },
          { value: 'md', label: 'Média' },
          { value: 'lg', label: 'Alta' },
        ],
      },
      {
        key: 'hover',
        label: 'Reagir ao passar o mouse',
        type: 'boolean',
        defaultValue: false,
        advanced: true,
      },
      RAIO,
      SOMBRA,
      { key: 'backgroundColor', label: 'Cor de fundo', type: 'color' },
      { key: 'textColor', label: 'Cor do texto', type: 'color' },
      MOVIMENTO,

    ],
  },

  {
    key: 'ui.avatars',
    label: 'Prova social com fotos',
    family: 'content',
    icon: 'users',
    outputs: TODAS,
    props: [
      {
        key: 'items',
        label: 'Fotos',
        type: 'richtext',
        defaultValue: '',
        help: 'Uma linha por pessoa: endereço da foto | nome.',
        syntax: 'Uma pessoa por linha: endereco_da_foto|nome. Sem foto, aparecem as iniciais.',
        example: 'acervo://capa.retrato|Ana Martins\n|Carlos Lima',
      },
      { key: 'text', label: 'Texto ao lado', type: 'text', defaultValue: '+2.000 clientes atendidos' },
      { key: 'note', label: 'Observação', type: 'text', defaultValue: 'Nota 4,9 de 5 em avaliações' },
      { key: 'align', label: 'Alinhamento', type: 'select', defaultValue: 'left', options: ALINHAMENTO },
      {
        key: 'max',
        label: 'Quantas fotos mostrar',
        type: 'text',
        defaultValue: '5',
        advanced: true,
        help: 'As pessoas que passarem desse total viram um contador "+N".',
      },
      {
        key: 'rating',
        label: 'Nota em estrelas',
        type: 'text',
        advanced: true,
        help: 'Ex.: 4,9 — aparece com estrelas ao lado do texto.',
      },
      MOVIMENTO,

    ],
  },

  {
    key: 'ui.stat',
    label: 'Número em destaque',
    family: 'content',
    icon: 'trending-up',
    outputs: TODAS,
    props: [
      { key: 'value', label: 'Número', type: 'text', defaultValue: '98%' },
      { key: 'label', label: 'Descrição', type: 'text', defaultValue: 'de clientes satisfeitos' },
      { key: 'align', label: 'Alinhamento', type: 'select', defaultValue: 'left', options: ALINHAMENTO },
      { key: 'accentColor', label: 'Cor do número', type: 'color' },
      MOVIMENTO,
    ],
  },

  {
    key: 'ui.logos',
    label: 'Faixa de logos',
    family: 'content',
    icon: 'building-2',
    outputs: TODAS,
    props: [
      {
        key: 'items',
        label: 'Logos',
        type: 'richtext',
        defaultValue: '',
        help: 'Uma linha por logo: endereço da imagem | nome.',
        syntax: 'Um logo por linha: endereco_da_imagem|nome.',
        example: 'https://exemplo.com/cliente-a.svg|Cliente A',
      },
      { key: 'note', label: 'Texto acima', type: 'text', defaultValue: 'Empresas que confiam' },
      { key: 'grayscale', label: 'Exibir em tons de cinza', type: 'boolean', defaultValue: true },
      { key: 'align', label: 'Alinhamento', type: 'select', defaultValue: 'left', options: ALINHAMENTO },
      MOVIMENTO,
    ],
  },

  {
    key: 'ui.device',
    label: 'Tela em moldura',
    family: 'content',
    icon: 'monitor',
    outputs: TODAS,
    props: [
      { key: 'src', label: 'Imagem da tela', type: 'image' },
      { key: 'alt', label: 'Descrição da imagem', type: 'text' },
      {
        key: 'device',
        label: 'Moldura',
        type: 'select',
        defaultValue: 'browser',
        options: [
          { value: 'browser', label: 'Navegador' },
          { value: 'laptop', label: 'Notebook' },
          { value: 'phone', label: 'Celular' },
        ],
      },
      SOMBRA,
      MOVIMENTO,
    ],
  },

  {
    key: 'ui.video',
    label: 'Vídeo',
    family: 'content',
    icon: 'play',
    outputs: SO_WEB,
    props: [
      {
        key: 'videoUrl',
        label: 'Endereço do vídeo',
        type: 'text',
        help: 'Cole o link normal do YouTube, Vimeo ou Loom (ou o endereço de um arquivo .mp4). O sistema converte sozinho.',
      },
      { key: 'posterUrl', label: 'Imagem de capa', type: 'image', help: 'Opcional. No YouTube, a capa do próprio vídeo é usada quando este campo fica vazio.' },
      {
        key: 'aspect',
        label: 'Formato do quadro',
        type: 'select',
        defaultValue: '16x9',
        options: [
          { value: '16x9', label: 'Widescreen (16:9)' },
          { value: '4x3', label: 'Clássico (4:3)' },
          { value: '1x1', label: 'Quadrado (1:1)' },
          { value: '9x16', label: 'Vertical (9:16)' },
        ],
      },
      { key: 'playLabel', label: 'Texto abaixo do play', type: 'text', defaultValue: 'Assistir', help: 'Deixe vazio para mostrar só o botão.' },
      RAIO,
      SOMBRA,
      MOVIMENTO,
    ],
  },
]);

// =============================================================================
// Camadas e posicionamento governado (Fase 3)
// =============================================================================

/**
 * Grupo "Camadas": onde o elemento fica, em que profundidade e o ajuste fino.
 * Fica no modo avançado — quem só quer montar a página não vê nada disso, e
 * documentos antigos (sem o grupo) continuam exatamente iguais.
 */
export const CAMADA: StudioBlockProp = {
  key: 'layer',
  label: 'Camadas e posição',
  type: 'group',
  advanced: true,
  help: 'Só tem efeito dentro de uma seção com "Camadas livres" ligadas. No celular volta ao fluxo normal.',
  fields: [
    {
      key: 'position',
      label: 'Onde fica',
      type: 'responsive',
      defaultValue: 'flow',
      of: { label: 'Posição', type: 'select', options: STUDIO_POSITION_OPTIONS },
    },
    { key: 'anchor', label: 'Ponto de ancoragem', type: 'select', defaultValue: 'top-left', options: STUDIO_ANCHOR_OPTIONS },
    { key: 'depth', label: 'Profundidade', type: 'select', defaultValue: 'content', options: STUDIO_DEPTH_OPTIONS },
    {
      key: 'offsetX',
      label: 'Deslocamento na horizontal',
      type: 'responsive',
      of: { label: 'Horizontal', type: 'text' },
      help: 'Número em pixels (ex.: -40) ou medida completa (ex.: 8%).',
    },
    {
      key: 'offsetY',
      label: 'Deslocamento na vertical',
      type: 'responsive',
      of: { label: 'Vertical', type: 'text' },
    },
    { key: 'rotate', label: 'Giro (graus)', type: 'responsive', of: { label: 'Giro', type: 'text' } },
    { key: 'scale', label: 'Escala', type: 'responsive', of: { label: 'Escala', type: 'text' }, help: '1 = tamanho normal; 1.1 = 10% maior.' },
    { key: 'opacity', label: 'Transparência', type: 'responsive', of: { label: 'Opacidade', type: 'text' }, help: '1 = opaco; 0.5 = metade.' },
    { key: 'stickyTop', label: 'Distância do topo ao grudar', type: 'text', defaultValue: '16px' },
    { key: 'keepOnMobile', label: 'Manter a posição no celular', type: 'boolean', defaultValue: false },
  ],
};

/**
 * Todos os primitivos `ui.*` recebem o grupo de camadas.
 * Feito aqui (e não repetido em cada bloco) para não duplicar definição.
 */
['ui.badge', 'ui.title', 'ui.paragraph', 'ui.buttons', 'ui.image', 'ui.card', 'ui.avatars', 'ui.stat', 'ui.logos', 'ui.device', 'ui.video'].forEach((chave) => {
  const definicao = getBlock(chave);
  if (!definicao || definicao.props.some((p) => p.key === 'layer')) return;
  registerBlock({ ...definicao, props: [...definicao.props, CAMADA] });
});

/** A seção com camadas ganha a chave que libera o posicionamento ancorado. */
(() => {
  const secao = getBlock('section.layered');
  if (!secao || secao.props.some((p) => p.key === 'allowLayers')) return;
  registerBlock({
    ...secao,
    props: [
      ...secao.props,
      {
        key: 'allowLayers',
        label: 'Camadas livres',
        type: 'boolean',
        defaultValue: false,
        advanced: true,
        help: 'Permite que os elementos desta seção sejam ancorados livremente (cartão flutuante, orbe decorativa).',
      },
    ],
  });
})();

/**
 * Efeito visual — o bloco apenas REFERENCIA um efeito do registro
 * (`src/lib/studio/core/effects.ts`). Quando o meio não suporta (PDF/e-mail),
 * o próprio registro troca pelo substituto declarado.
 */
export const EFEITO: StudioBlockProp = {
  key: 'effect',
  label: 'Efeito visual',
  type: 'select',
  defaultValue: 'none',
  advanced: true,
  options: STUDIO_EFFECT_OPTIONS,
  help: 'Efeitos sem suporte no PDF ou no e-mail trocam sozinhos por uma versão equivalente.',
};

/** Aplica o efeito aos primitivos e à seção com camadas, sem duplicar definição. */
['section.layered', 'ui.badge', 'ui.title', 'ui.paragraph', 'ui.buttons', 'ui.image', 'ui.card', 'ui.avatars', 'ui.stat', 'ui.logos', 'ui.device', 'ui.video'].forEach((chave) => {
  const definicao = getBlock(chave);
  if (!definicao || definicao.props.some((p) => p.key === 'effect')) return;
  registerBlock({ ...definicao, props: [...definicao.props, EFEITO] });
});

/**
 * Ajuste fino do movimento (disparo, atraso e velocidade) em todo bloco que já
 * oferece a propriedade "Movimento". Fica no painel avançado para não poluir a
 * edição do dia a dia.
 */
listBlocks().forEach((definicao) => {
  if (!definicao.props.some((p) => p.key === 'motion')) return;
  if (definicao.props.some((p) => p.key === 'motionTrigger')) return;
  registerBlock({
    ...definicao,
    props: [...definicao.props, MOVIMENTO_DISPARO, MOVIMENTO_ATRASO, MOVIMENTO_DURACAO],
  });
});

// =============================================================================
// ACABAMENTO VISUAL E FORMAS DECORATIVAS (Fase 6)
// =============================================================================

/**
 * Recorte da mídia — o bloco apenas REFERENCIA um recorte do registro
 * (`src/lib/studio/core/visual.ts`). Onde o meio não suporta, o próprio
 * registro troca pelo substituto declarado (ou sai sem recorte).
 */
export const RECORTE: StudioBlockProp = {
  key: 'mask',
  label: 'Recorte',
  type: 'select',
  defaultValue: 'none',
  advanced: true,
  options: STUDIO_MASK_OPTIONS,
  help: 'Formato da mídia. Na impressão e no e-mail, recortes sem suporte saem em versão equivalente.',
};

/** Traçado do desenho próprio, usado apenas quando o recorte é "Desenho próprio". */
export const RECORTE_TRACADO: StudioBlockProp = {
  key: 'maskPath',
  label: 'Traçado do desenho',
  type: 'text',
  advanced: true,
  help: 'Só o traçado (ex.: M0,0 L100,0 L100,80 Z). Conteúdo fora desse formato é descartado por segurança.',
};

/** Inclinação leve em perspectiva — dá profundidade sem mexer no layout. */
export const INCLINACAO: StudioBlockProp = {
  key: 'tilt',
  label: 'Inclinação',
  type: 'select',
  defaultValue: 'none',
  advanced: true,
  options: STUDIO_TILT_OPTIONS,
  help: 'Efeito de perspectiva. Desligado na impressão e no e-mail.',
};

/** Aplica o acabamento visual aos blocos de mídia, sem duplicar definição. */
['ui.image', 'ui.card', 'ui.device', 'ui.video'].forEach((chave) => {
  const definicao = getBlock(chave);
  if (!definicao || definicao.props.some((p) => p.key === 'mask')) return;
  registerBlock({
    ...definicao,
    props: [...definicao.props, RECORTE, RECORTE_TRACADO, INCLINACAO],
  });
});

/**
 * Forma decorativa — enfeite de composição (orbe, anel, feixe, linha, onda,
 * grade). Vive DENTRO de uma seção com camadas, normalmente ancorada ao fundo.
 * É só enfeite: na impressão e no e-mail simplesmente não aparece.
 */
registerBlock({
  key: 'ui.shape',
  label: 'Forma decorativa',
  family: 'content',
  icon: 'circle',
  outputs: SO_WEB,
  props: [
    {
      key: 'shape',
      label: 'Forma',
      type: 'select',
      defaultValue: 'orb',
      options: STUDIO_SHAPE_OPTIONS,
    },
    {
      key: 'size',
      label: 'Tamanho',
      type: 'select',
      defaultValue: 'md',
      options: STUDIO_SHAPE_SIZE_OPTIONS,
    },
    { key: 'color', label: 'Cor', type: 'color' },
    MOVIMENTO,
    MOVIMENTO_DISPARO,
    MOVIMENTO_ATRASO,
    MOVIMENTO_DURACAO,
    CAMADA,
  ],
});
